import { Loading } from "@/components";
import Modal from "@/components/common/Modal";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { getIsLoading, setIsApiLoading, setIsLoading } from "@/store/appSlice";
import API from "@/utils/api";
import { DICTATION_COURSE_TYPE_TEXT } from "@/utils/constants";
import { yupResolver } from "@hookform/resolvers/yup";
import DT from "datatables.net-dt";
import DataTable from "datatables.net-react";
import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import * as yup from "yup";

DataTable.use(DT);

type DictationCourse = {
  id: string;
  name?: string;
  courseName?: string;
  displayOrder: number;
  level: number;
  description?: string;
  courseDescription?: string;
  type?: string;
  typeText?: string;
  note?: string;
  totalLesson?: number;
  hasCategories?: boolean;
};

const courseSchema = yup.object().shape({
  name: yup.string().required("コース名が必須です。"),
  displayOrder: yup.number().required("表示順が必須です。").min(1),
  level: yup.number().required("レベルが必須です。").min(1).max(5),
  description: yup.string(),
});

const DictationCourses = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(getIsLoading);
  const [courses, setCourses] = useState<DictationCourse[]>([]);
  const [activeCourse, setActiveCourse] = useState<DictationCourse | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(courseSchema),
  });

  const fetchCourses = async () => {
    dispatch(setIsLoading(true));
    try {
      const response = await API.get("dictation-courses");
      console.log("API Response:", response.data); // Debug log
      
      // Handle different response formats
      let coursesData = [];
      if (response.data && response.data.data) {
        coursesData = response.data.data;
      } else if (response.data && Array.isArray(response.data)) {
        coursesData = response.data;
      }
      
      console.log("Courses data:", coursesData); // Debug log
      
      // Ensure coursesData is an array and has proper structure with all required fields
      const validCourses = Array.isArray(coursesData) ? coursesData.filter(course => 
        course && typeof course === 'object' && course.id
      ).map(course => ({
        // Ensure all required fields exist with defaults
        id: course.id,
        name: course.name || course.courseName || "",
        courseName: course.courseName || course.name || "",
        displayOrder: Number(course.displayOrder) || 0,
        level: Number(course.level) || 1,
        description: course.description || course.courseDescription || "",
        courseDescription: course.courseDescription || course.description || "",
        type: course.type || "custom",
        typeText: course.typeText || "",
        note: course.note || "",
        totalLesson: Number(course.totalLesson) || 0,
        hasCategories: Boolean(course.hasCategories),
      })) : [];
      
      setCourses(validCourses);
      
      // Make courses available globally for event handlers
      (window as any).courses = validCourses;
      (window as any).openEditModal = openEditModal;
    } catch (err: any) {
      console.error("Error fetching courses:", err);
      console.error("Error response:", err.response?.data);
      toast.error("コースの取得に失敗しました。");
      setCourses([]); // Set empty array on error
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const handleCreateCourse = async (data: any) => {
    try {
      dispatch(setIsApiLoading(true));
      await API.post("dictation-course", data);
      toast.success("コースが作成されました。");
      fetchCourses();
      reset();
      document.querySelector<HTMLButtonElement>("#modalCourse .btn-close")?.click();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "コースの作成に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const handleUpdateCourse = async (data: any) => {
    if (!activeCourse) return;
    
    try {
      dispatch(setIsApiLoading(true));
      await API.put(`dictation-course/${activeCourse.id}`, data);
      toast.success("コースが更新されました。");
      fetchCourses();
      reset();
      document.querySelector<HTMLButtonElement>("#modalCourse .btn-close")?.click();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "コースの更新に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const openCreateModal = () => {
    setIsEditMode(false);
    setActiveCourse(null);
    reset({
      name: "",
      displayOrder: courses.length + 1,
      level: 1,
      description: "",
    });
  };

  const openEditModal = (course: DictationCourse) => {
    setIsEditMode(true);
    setActiveCourse(course);
    reset({
      name: course.courseName || course.name || "",
      displayOrder: course.displayOrder || 1,
      level: course.level || 1,
      description: course.courseDescription || course.description || "",
    });
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  // Setup event listeners after courses are loaded
  useEffect(() => {
    const handleClick = (e: Event) => {
      const target = e.target as HTMLElement;
      
      if (target.closest(".detail-btn")) {
        const id = target.closest(".detail-btn")?.getAttribute("data-id");
        if (id) {
          window.location.href = `/content/dictation-courses/${id}`;
        }
      }
    };

    document.addEventListener("click", handleClick);
    
    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, [courses]); // Re-setup when courses change

  return (
    <>
      <Helmet>
        <title>ディクテーションコース管理</title>
      </Helmet>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h4 className="card-title">ディクテーションコース管理</h4>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-primary"
                    data-bs-toggle="modal"
                    data-bs-target="#modalCourse"
                    onClick={openCreateModal}
                  >
                    <i className="fas fa-plus me-2"></i>
                    新規作成
                  </button>
                </div>
              </div>
              <div className="card-body">
                <Loading />
                {!loading && courses.length > 0 && (
                  <div className="table-responsive">
                    <DataTable
                      data={courses}
                      className="display"
                      options={{
                        autoWidth: true,
                        columnDefs: [
                          { targets: "_all", className: "text-start" },
                        ],
                        stripeClasses: [],
                        order: [[0, "asc"]], // Sort by Course ID
                        searching: true,
                        destroy: true,
                        info: true,
                        paging: true,
                        language: {
                          lengthMenu: "レコード数&nbsp;_MENU_",
                          emptyTable: "データがありません。",
                          search: "検索:",
                          paginate: {
                            first: "最初",
                            last: "最後",
                            next: "次へ",
                            previous: "前へ",
                          },
                        },
                      }}
                      columns={[
                        {
                          title: "コースID",
                          data: "id",
                          className: "text-start",
                          orderable: true,
                        },
                        {
                          title: "コース名",
                          data: null,
                          className: "text-start",
                          orderable: true,
                          render: (data, type, row) => {
                            return row.courseName || row.name || "-";
                          },
                        },
                        {
                          title: "総レッスン数",
                          data: null,
                          className: "text-center",
                          orderable: true,
                          render: (data, type, row) => {
                            return row.totalLesson || 0;
                          },
                        },
                        {
                          title: "タイプ",
                          data: null,
                          className: "text-center",
                          orderable: true,
                          render: (data, type, row) => {
                            return row.typeText || DICTATION_COURSE_TYPE_TEXT[row.type] || row.type || "-";
                          },
                        },
                        {
                          title: "説明",
                          data: null,
                          className: "text-start",
                          orderable: false,
                          render: (data, type, row) => {
                            return row.courseDescription || row.description || "-";
                          },
                        },
                        {
                          title: "操作",
                          data: null,
                          orderable: false,
                          className: "text-center",
                          render: (data, type, row) => {
                            return `
                              <div class="d-flex justify-content-center gap-2">
                                <button class="btn btn-sm btn-outline-primary detail-btn" data-id="${row.id}">
                                  <i class="fas fa-edit"></i>
                                </button>
                              </div>
                            `;
                          },
                        },
                      ]}
                    />
                  </div>
                )}
                {!loading && courses.length === 0 && (
                  <div className="text-center py-4">
                    <p className="text-muted">データがありません。</p>
                    <button
                      className="btn btn-primary"
                      data-bs-toggle="modal"
                      data-bs-target="#modalCourse"
                      onClick={openCreateModal}
                    >
                      <i className="fas fa-plus me-2"></i>
                      最初のコースを作成
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <Modal
          id="modalCourse"
          title={isEditMode ? "コース編集" : "コース作成"}
        >
          <Form onSubmit={handleSubmit(isEditMode ? handleUpdateCourse : handleCreateCourse)}>
            <Form.Group className="mb-3">
              <Form.Label>コース名 <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="コース名を入力"
                isInvalid={!!errors.name}
                {...register("name")}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>表示順 <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                min="1"
                isInvalid={!!errors.displayOrder}
                {...register("displayOrder")}
              />
              <Form.Control.Feedback type="invalid">
                {errors.displayOrder?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>レベル <span className="text-danger">*</span></Form.Label>
              <Form.Select
                isInvalid={!!errors.level}
                {...register("level")}
              >
                <option value="1">1 - 初級</option>
                <option value="2">2 - 初中級</option>
                <option value="3">3 - 中級</option>
                <option value="4">4 - 中上級</option>
                <option value="5">5 - 上級</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.level?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>説明</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="コースの説明を入力"
                {...register("description")}
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                キャンセル
              </button>
              <button type="submit" className="btn btn-primary">
                {isEditMode ? "更新" : "作成"}
              </button>
            </div>
          </Form>
        </Modal>
      </div>
    </>
  );
};

export default DictationCourses;